import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  CreditCard, 
  DollarSign, 
  Users, 
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  Receipt,
  ArrowUpRight,
  ArrowDownLeft,
  Settings,
  FileText,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  closed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

const PAYMENT_TERMS = [
  { value: 'net_7', label: 'Net 7 Days' },
  { value: 'net_14', label: 'Net 14 Days' },
  { value: 'net_30', label: 'Net 30 Days' },
  { value: 'net_60', label: 'Net 60 Days' },
  { value: 'cod', label: 'Cash on Delivery' },
];

export default function AdminCreditAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('accounts');
  
  const [form, setForm] = useState({
    customer_id: '',
    credit_limit: '',
    payment_terms: 'net_30',
    notes: ''
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    reference: '',
    description: ''
  });
  
  const [settings, setSettings] = useState({
    statement_day: 1,
    overdue_reminder_days: [7, 14, 30],
    auto_suspend_days: 60,
    default_payment_terms: 'net_30'
  });

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchAccounts();
    fetchCustomers();
    fetchStatements();
    fetchSettings();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/credit-accounts`, authHeaders);
      setAccounts(res.data.accounts || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error('Failed to load credit accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/all`, authHeaders);
      // Filter out customers who already have credit accounts
      const existingAccountIds = accounts.map(a => a.customer_id);
      setCustomers((res.data.customers || []).filter(c => !existingAccountIds.includes(c.id)));
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const fetchStatements = async () => {
    try {
      const res = await axios.get(`${API}/admin/statements`, authHeaders);
      setStatements(res.data.statements || []);
    } catch (err) {
      console.error('Failed to load statements');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/account-settings`, authHeaders);
      setSettings(res.data.settings || settings);
    } catch (err) {
      console.error('Failed to load settings');
    }
  };

  const fetchAccountDetail = async (accountId) => {
    try {
      const res = await axios.get(`${API}/admin/credit-accounts/${accountId}`, authHeaders);
      setSelectedAccount(res.data.account);
      setTransactions(res.data.transactions || []);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load account details');
    }
  };

  const handleCreate = async () => {
    if (!form.customer_id || !form.credit_limit) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/credit-accounts`, {
        customer_id: form.customer_id,
        credit_limit: parseFloat(form.credit_limit),
        payment_terms: form.payment_terms,
        notes: form.notes
      }, authHeaders);
      
      toast.success('Credit account created');
      setShowCreateModal(false);
      setForm({ customer_id: '', credit_limit: '', payment_terms: 'net_30', notes: '' });
      fetchAccounts();
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleUpdate = async (accountId, updates) => {
    try {
      await axios.put(`${API}/admin/credit-accounts/${accountId}`, updates, authHeaders);
      toast.success('Account updated');
      fetchAccounts();
      if (showDetailModal) {
        fetchAccountDetail(accountId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || !paymentForm.reference) {
      toast.error('Please enter amount and reference');
      return;
    }

    try {
      await axios.post(`${API}/admin/credit-accounts/${selectedAccount.id}/payment`, {
        amount: parseFloat(paymentForm.amount),
        reference: paymentForm.reference,
        description: paymentForm.description
      }, authHeaders);
      
      toast.success('Payment recorded');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', reference: '', description: '' });
      fetchAccounts();
      if (showDetailModal) {
        fetchAccountDetail(selectedAccount.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this credit account? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/credit-accounts/${accountId}`, authHeaders);
      toast.success('Account deleted');
      fetchAccounts();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleGenerateStatements = async () => {
    try {
      const res = await axios.post(`${API}/admin/statements/generate`, {}, authHeaders);
      toast.success(`Generated ${res.data.generated} statements`);
      fetchStatements();
    } catch (err) {
      toast.error('Failed to generate statements');
    }
  };

  const handleSendStatement = async (statementId) => {
    try {
      await axios.post(`${API}/admin/statements/${statementId}/send`, {}, authHeaders);
      toast.success('Statement sent');
      fetchStatements();
    } catch (err) {
      toast.error('Failed to send statement');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/account-settings`, { settings }, authHeaders);
      toast.success('Settings saved');
      setShowSettingsModal(false);
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="credit-accounts-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <CreditCard className="w-8 h-8 text-[#78BE20]" />
              Credit Accounts
            </h1>
            <p className="text-muted-foreground">Manage customer credit accounts and statements</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-[#78BE20] hover:bg-[#78BE20]/90">
              <Plus className="w-4 h-4 mr-2" />
              New Credit Account
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#78BE20]" />
                <span className="text-sm text-muted-foreground">Total Accounts</span>
              </div>
              <div className="text-2xl font-bold mt-2">{stats.total || 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-400" />
                <span className="text-sm text-green-400/70">Active</span>
              </div>
              <div className="text-2xl font-bold text-green-400 mt-2">{stats.active || 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-blue-400/70">Total Credit</span>
              </div>
              <div className="text-2xl font-bold text-blue-400 mt-2">${(stats.total_credit_limit || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                <span className="text-sm text-orange-400/70">Outstanding</span>
              </div>
              <div className="text-2xl font-bold text-orange-400 mt-2">${(stats.total_outstanding || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800">
            <TabsTrigger value="accounts">Credit Accounts</TabsTrigger>
            <TabsTrigger value="statements">Statements</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : accounts.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Credit Accounts</h3>
                  <p className="text-muted-foreground mb-4">Create credit accounts for approved customers</p>
                  <Button onClick={() => setShowCreateModal(true)} className="bg-[#78BE20]">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Account
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Credit Limit</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Balance</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Available</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Terms</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(account => (
                      <tr key={account.id} className="border-t border-zinc-800 hover:bg-zinc-800/30">
                        <td className="p-4">
                          <div className="font-medium">{account.customer_name}</div>
                          <div className="text-sm text-muted-foreground">{account.customer_email}</div>
                        </td>
                        <td className="p-4 font-mono">${account.credit_limit?.toLocaleString()}</td>
                        <td className="p-4 font-mono">
                          <span className={account.current_balance > 0 ? 'text-orange-400' : 'text-green-400'}>
                            ${account.current_balance?.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-4 font-mono text-green-400">${account.available_credit?.toLocaleString()}</td>
                        <td className="p-4">
                          <Badge variant="outline" className="text-xs">
                            {account.payment_terms?.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <Badge className={statusColors[account.status]}>
                            {account.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => fetchAccountDetail(account.id)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => { setSelectedAccount(account); setShowPaymentModal(true); }}
                              className="text-green-400"
                            >
                              <DollarSign className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="statements" className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Account Statements</h3>
              <Button onClick={handleGenerateStatements} className="bg-[#78BE20]">
                <FileText className="w-4 h-4 mr-2" />
                Generate Statements
              </Button>
            </div>
            
            {statements.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center text-muted-foreground">
                  No statements generated yet
                </CardContent>
              </Card>
            ) : (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Statement #</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Period</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Closing Balance</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map(stmt => (
                      <tr key={stmt.id} className="border-t border-zinc-800 hover:bg-zinc-800/30">
                        <td className="p-4 font-mono">{stmt.statement_number}</td>
                        <td className="p-4">{stmt.customer_name}</td>
                        <td className="p-4 text-sm">{stmt.period_start} to {stmt.period_end}</td>
                        <td className="p-4 font-mono text-orange-400">${stmt.closing_balance?.toLocaleString()}</td>
                        <td className="p-4">
                          <Badge variant={stmt.status === 'sent' ? 'default' : 'outline'}>
                            {stmt.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            {stmt.status !== 'sent' && (
                              <Button variant="ghost" size="sm" onClick={() => handleSendStatement(stmt.id)}>
                                <Send className="h-4 w-4 mr-1" />
                                Send
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Create Credit Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || c.email} ({c.account_tier})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Limit *</Label>
                <Input
                  type="number"
                  value={form.credit_limit}
                  onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                  placeholder="5000"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#78BE20]">Create Account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
            <DialogHeader>
              <DialogTitle>Credit Account Details</DialogTitle>
            </DialogHeader>
            {selectedAccount && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Customer</Label>
                    <p className="font-medium">{selectedAccount.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedAccount.customer_email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <Select 
                        value={selectedAccount.status} 
                        onValueChange={(v) => handleUpdate(selectedAccount.id, { status: v })}
                      >
                        <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4 p-4 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-muted-foreground text-xs">Credit Limit</Label>
                    <p className="text-xl font-bold">${selectedAccount.credit_limit?.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Current Balance</Label>
                    <p className="text-xl font-bold text-orange-400">${selectedAccount.current_balance?.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Available</Label>
                    <p className="text-xl font-bold text-green-400">${selectedAccount.available_credit?.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Terms</Label>
                    <p className="text-xl font-bold">{selectedAccount.payment_terms?.replace('_', ' ').toUpperCase()}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Recent Transactions</Label>
                  <div className="mt-2 max-h-60 overflow-auto">
                    {transactions.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-4 text-center">No transactions yet</p>
                    ) : (
                      <div className="space-y-2">
                        {transactions.map(t => (
                          <div key={t.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded">
                            <div className="flex items-center gap-3">
                              {t.type === 'payment' ? (
                                <ArrowDownLeft className="w-4 h-4 text-green-400" />
                              ) : (
                                <ArrowUpRight className="w-4 h-4 text-orange-400" />
                              )}
                              <div>
                                <p className="text-sm font-medium">{t.description}</p>
                                <p className="text-xs text-muted-foreground">{formatDate(t.created_at)} • {t.reference}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-mono ${t.amount < 0 ? 'text-green-400' : 'text-orange-400'}`}>
                                {t.amount < 0 ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">Balance: ${t.balance_after?.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline" 
                className="text-red-400 border-red-500/30"
                onClick={() => handleDelete(selectedAccount?.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <Button 
                className="bg-green-600"
                onClick={() => { setShowDetailModal(false); setShowPaymentModal(true); }}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            {selectedAccount && (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Recording payment for</p>
                  <p className="font-medium">{selectedAccount.customer_name}</p>
                  <p className="text-lg text-orange-400 mt-2">
                    Current Balance: ${selectedAccount.current_balance?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label>Reference *</Label>
                  <Input
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                    placeholder="e.g., Bank transfer 12345"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                    placeholder="Optional description"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button onClick={handleRecordPayment} className="bg-green-600">Record Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Modal */}
        <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Credit Account Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Statement Day of Month</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={settings.statement_day}
                  onChange={(e) => setSettings({ ...settings, statement_day: parseInt(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700"
                />
                <p className="text-xs text-muted-foreground mt-1">Statements will be auto-generated on this day</p>
              </div>
              <div>
                <Label>Overdue Reminder Days</Label>
                <Input
                  value={settings.overdue_reminder_days?.join(', ')}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    overdue_reminder_days: e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d))
                  })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="7, 14, 30"
                />
                <p className="text-xs text-muted-foreground mt-1">Days after due date to send reminders (comma separated)</p>
              </div>
              <div>
                <Label>Auto-Suspend After (Days)</Label>
                <Input
                  type="number"
                  value={settings.auto_suspend_days}
                  onChange={(e) => setSettings({ ...settings, auto_suspend_days: parseInt(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700"
                />
                <p className="text-xs text-muted-foreground mt-1">Accounts will be suspended after this many days overdue</p>
              </div>
              <div>
                <Label>Default Payment Terms</Label>
                <Select 
                  value={settings.default_payment_terms} 
                  onValueChange={(v) => setSettings({ ...settings, default_payment_terms: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} className="bg-[#78BE20]">Save Settings</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
